import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import useCompanyListQuery from '@features/company/hooks';
import { useGetTeamByadminQuery, useAdminDisabledTeamListQuery } from '@features/team/hooks';
import useAuthStore from '@entities/auth/model/useAuthStore';
import { postLogout } from '@entities/auth/api/auth';

import OrganizationAdminTree from '@pages/admin/organization/components/OrganizationAdminTree'; // 왼쪽 tree 컴포넌트
import OrganizationDetailPanel from '@pages/admin/organization/components/OrganizationDetailPanel';
import DisabledOrganizationList from '@pages/admin/organization/components/DisabledOrganizationList';
import Alert from '@shared/components/atoms/Alert';
import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';

import styles from '@pages/admin/organization/OrganizationManagementPage.module.scss';

/* ============================================================
 * 유틸: 회사 목록 중복 제거
 *
 * - 로그인 회사 기준으로 회사 정보를 가공
 * - Tree 컴포넌트에서 사용하기 위한 형태로 정리
 * ============================================================ */
function uniqCompanies(list) {
  const map = new Map();

  list.forEach((item) => {
    if (!map.has(item.companyCode)) {
      map.set(item.companyCode, {
        companyCode: item.companyCode,
        companyName: item.company,
        nick: item.nick,
        ceoName: item.name,
        position: item.position,
        enumber: item.enumber,
      });
    }
  });

  return Array.from(map.values());
}

/* ============================================================
 * Pending Action 타입 정의
 *
 * - "dirty 상태에서 발생한 이탈 요청"을
 *   confirm 이후에 실행하기 위해 임시 저장하는 액션들
 * ============================================================ */
const PENDING_ACTION = {
  NONE: 'none',

  // 조직도 내 다른 부서 클릭
  NODE: 'node',

  // '미사용 조직 보기' 토글 클릭
  TOGGLE_DISABLED: 'toggleDisabled',

  // 사이드바(LNB) 메뉴 이동
  NAVIGATION: 'navigation',

  // 로그아웃 요청
  LOGOUT: 'logout',
};
// 아무 동작도 대기 중이지 않은 기본 상태
const EMPTY_ACTION = { type: 'none' };

export default function OrganizationManagementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((state) => state.clearAuth);

  /* ============================================================
   * 로그아웃 실제 실행 로직
   *
   * - confirm 이후에만 호출됨
   * - 서버 실패 여부와 관계없이
   *   클라이언트 상태는 반드시 초기화
   * ============================================================ */
  async function executeLogout() {
    try {
      await postLogout();
    } catch {
      // 서버 로그아웃 실패는 무시
    } finally {
      queryClient.clear();
      clearAuth();
      navigate('/login');
    }
  }

  /* ===========================
     트리 상태
  ============================ */
  const [addingNodeKey, setAddingNodeKey] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  // '미사용 조직 보기' 체크 여부
  const [showDisabled, setShowDisabled] = useState(false);
  // 상세 패널에서 수정 여부
  const [isDirty, setIsDirty] = useState(false);

  /* ============================================================
   * Alert / Confirm 상태
   *
   * - alertOpen      : 단순 알림
   * - confirmOpen    : dirty 상태에서 이동 시 확인용
   * - pendingAction  : confirm 이후 실행할 실제 동작
   * ============================================================ */
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [afterAlertClose, setAfterAlertClose] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(EMPTY_ACTION);

  function openAlert(message, afterClose) {
    setAlertMessage(message);
    setAfterAlertClose(() => afterClose);
    setAlertOpen(true);
  }

  /* ===========================
     데이터 조회
  ============================ */
  const loginCompanyCode = useAuthStore((state) => state.companyCode);
  const { data: companyList = [] } = useCompanyListQuery();

  const companies = useMemo(() => {
    return uniqCompanies(companyList.filter((company) => company.companyCode === loginCompanyCode));
  }, [companyList, loginCompanyCode]);

  const { data: teamQueries } = useGetTeamByadminQuery(loginCompanyCode);

  const teamList = useMemo(() => {
    const list = teamQueries?.resultData?.teamList ?? [];

    // 운영 중(enabled === 0)인 부서만 표시
    return list.filter((team) => team.enabled === 0);
  }, [teamQueries]);

  /* ===========================
     부서 생성 성공 처리
  ============================ */
  const handleTeamCreateSuccess = () => {
    openAlert('부서가 생성되었습니다.', () => {
      setAddingNodeKey(null); // "새 부서 추가 중입니다" 닫기
      setSelectedNode(null); // 상세 패널 초기화
    });
  };

  /* ============================================================
   * 미사용 조직 목록 처리
   * ============================================================ */
  const { data: disabledTeamQuery } = useAdminDisabledTeamListQuery(loginCompanyCode, {
    enabled: showDisabled,
  });

  const disabledTeamList = disabledTeamQuery?.resultData?.teamList ?? [];
  // 미사용 조직 목록에서 사라진 부서를 보고 있었다면 상세 패널 초기화
  useEffect(() => {
    // 왼쪽이 "미사용 조직 보기" 상태일 때만 처리
    if (!showDisabled) return;
    if (!selectedNode) return;
    if (selectedNode.nodeType !== 'TEAM') return;

    const exists = disabledTeamList.some((team) => team.teamCode === selectedNode.key);

    // 미사용 목록에서 사라졌다면 → 오른쪽 초기화
    if (!exists) {
      setSelectedNode(null);
    }
  }, [showDisabled, disabledTeamList, selectedNode]);

  /* ============================================================
   * 조직도 클릭 처리
   *
   * - dirty 아닐 때: 즉시 이동
   * - dirty 일 때  : confirm 후 이동
   * ============================================================ */
  function handleSelectNode(nextNode) {
    if (!isDirty) {
      setSelectedNode(nextNode);
      return;
    }

    setPendingAction({
      type: PENDING_ACTION.NODE,
      node: nextNode,
    });
    setConfirmOpen(true);
  }

  /* ============================================================
   * 전역 leave-request 이벤트 수신
   *
   * - 로그아웃
   * - 사이드바 이동
   * 등 모든 "페이지 이탈"을 단일 진입점에서 처리
   * ============================================================ */
  useEffect(() => {
    function handleLeaveRequest(e) {
      if (!isDirty) return;

      e.preventDefault();

      const { type, path } = e.detail || {};

      if (type === 'logout') {
        setPendingAction({ type: PENDING_ACTION.LOGOUT });
      }

      if (type === 'navigation') {
        setPendingAction({
          type: PENDING_ACTION.NAVIGATION,
          path,
        });
      }

      setConfirmOpen(true);
    }

    window.addEventListener('app:leave-request', handleLeaveRequest);
    return () => window.removeEventListener('app:leave-request', handleLeaveRequest);
  }, [isDirty]);

  /* ============================================================
   * confirm 확인 처리
   *
   * - dirty 해제
   * - pendingAction에 따라 실제 동작 수행
   * ============================================================ */
  function handleConfirm() {
    setIsDirty(false);
    setAddingNodeKey(null);

    switch (pendingAction.type) {
      case PENDING_ACTION.NODE:
        setSelectedNode(pendingAction.node);
        break;

      case PENDING_ACTION.TOGGLE_DISABLED:
        setShowDisabled(pendingAction.value);
        setSelectedNode(null);
        break;

      case PENDING_ACTION.NAVIGATION:
        navigate(pendingAction.path);
        break;

      case PENDING_ACTION.LOGOUT:
        executeLogout();
        break;

      default:
        break;
    }

    setPendingAction(EMPTY_ACTION);
    setConfirmOpen(false);
  }

  function handleCancel() {
    setPendingAction(EMPTY_ACTION);
    setConfirmOpen(false);
  }

  return (
    <AdminLayout>
      <TabContentLayout
        tabs={{
          label: '조직관리',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              <div className={styles.organizationLayout}>
                {/* 왼쪽: 트리 */}
                <div className={styles.treePanel}>
                  <div className={styles.treeHeader}>
                    <div className={styles.treeHeaderLeft}>
                      <div className={styles.treeTitle}>
                        {showDisabled ? '미사용 조직' : '조직도'}
                      </div>
                    </div>

                    <div className={styles.treeHeaderRight}>
                      <label className={styles.unusedToggle}>
                        <input
                          type="checkbox"
                          checked={showDisabled}
                          onChange={(e) => {
                            const nextValue = e.target.checked;

                            if (!isDirty) {
                              setShowDisabled(nextValue);
                              setSelectedNode(null);
                              return;
                            }

                            setPendingAction({
                              type: PENDING_ACTION.TOGGLE_DISABLED,
                              value: nextValue,
                            });
                            setConfirmOpen(true);
                          }}
                        />
                        <span>미사용 조직 보기</span>
                      </label>
                    </div>
                  </div>
                  <div className={styles.treeBody}>
                    {showDisabled ? (
                      <DisabledOrganizationList
                        teams={disabledTeamList}
                        selectedNode={selectedNode}
                        onSelectNode={setSelectedNode}
                      />
                    ) : (
                      <OrganizationAdminTree
                        companies={companies}
                        teamQueries={teamList}
                        onSelectNode={handleSelectNode} //선택 노드 변경
                        selectedNode={selectedNode} // 선택된 노드
                        addingNodeKey={addingNodeKey} // 추가중인 부모 노드
                        setAddingNodeKey={setAddingNodeKey}
                      />
                    )}
                  </div>
                </div>

                {/* 오른쪽: 상세 영역 */}
                <div className={styles.detailPanel}>
                  <OrganizationDetailPanel
                    teamList={teamList}
                    selectedNode={selectedNode}
                    onTeamCreateSuccess={handleTeamCreateSuccess}
                    onTeamDisabled={() => {
                      setSelectedNode(null);
                    }}
                    onOpenAlert={openAlert}
                    onDirtyChange={(dirty) => {
                      setIsDirty(dirty);
                    }}
                    companyList={companyList}
                  />
                </div>
              </div>
            </ContentCard>
          ),
        }}
      />
      {/* =====================
          공통 Alert (최상단)
      ====================== */}
      <Alert
        isOpen={alertOpen}
        message={alertMessage}
        onClose={() => {
          setAlertOpen(false);

          if (afterAlertClose) {
            afterAlertClose();
            setAfterAlertClose(null);
          }
        }}
      />
      <Alert
        isOpen={confirmOpen}
        mode="confirm"
        message="작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?"
        onConfirm={handleConfirm}
        onClose={handleCancel}
      />
    </AdminLayout>
  );
}
